import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import * as refValSheetActions from './reference-value-sheet.actions';

import { PoultryContextKeys } from '../../shared/constants';
import { SettingsServiceHelper } from '../../shared/services/settingsHelperService';
import {
  AnimalType,
  ColumnSettings,
  ReferenceValueSheet,
  ReferenceValueSheetDataSeries,
  ReferenceValueSheetKPIResults,
  RefValSheetModuleState,
  UpsertRefValSheetResponse,
} from '../models/reference-value-sheet.model';
import { ReferenceValueSheetService } from '../services/reference-value-sheet.service';

@Injectable()
export class ReferenceValueSheetEffects {
  constructor(
    private actions$: Actions,
    private store: Store<RefValSheetModuleState>,
    private router: Router,
    private dialogService: DialogService,
    @Inject(LocaleService) private locale: LocaleService,
    private refValSheetService: ReferenceValueSheetService,
    private settingsService: SettingsServiceHelper,
  ) {}

  /* LOAD DATA EFFECTS */
  loadRefValSheetData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadRefValSheetData),
      concatMap(() =>
        of(
          refValSheetActions.clearRefValSheetState(),
          refValSheetActions.loadRefValSheetsRequest(),
          refValSheetActions.loadColumnSettings(),
        ),
      ),
    ),
  );

  loadRefValSheetsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadRefValSheetsRequest),
      mergeMap((_) =>
        this.refValSheetService.getReferenceValueSheets('NL', AnimalType.REARING).pipe(
          switchMap((refValSheets: ReferenceValueSheet[]) =>
            of(refValSheetActions.loadRefValSheetsSuccess({ refValSheets })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.loadRefValSheetsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadRefValSheetKPIsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadRefValSheetKPIsRequest),
      mergeMap((action) =>
        forkJoin(
          action.ids.map((id) => this.refValSheetService.getReferenceValueSheetKpis(id)),
        ).pipe(
          map((results: ReferenceValueSheetKPIResults[]) =>
            refValSheetActions.loadRefValSheetKPIsSuccess({
              refValSheetKPIResults: results,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.loadRefValSheetDataSeriesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadRefValSheetsRequestDataSeries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadRefValSheetDataSeriesRequest),
      mergeMap((action) =>
        forkJoin(
          action.ids.map((id) => this.refValSheetService.getReferenceValueSheetDataSeriesById(id)),
        ).pipe(
          map((responses: ReferenceValueSheetDataSeries[]) =>
            refValSheetActions.loadRefValSheetDataSeriesSuccess({
              refValSheetDataSeries: responses,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.loadRefValSheetDataSeriesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadColumnSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadColumnSettings),
      switchMap((_) =>
        this.settingsService
          .getSingle(
            ColumnSettings.COLUMN_SETTINGS_ARRAY_DEFAULT,
            PoultryContextKeys.RVS_COLUMN_SETTINGS,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            map((value: string[]) =>
              refValSheetActions.setColumnSettings({
                value,
                save: false,
              }),
            ),
          ),
      ),
      catchError((_: HttpErrorResponse) => of(null)),
    ),
  );

  /* NAVIGATION EFFECTS */
  createRefValSheetNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.createRefValSheetNavigation),
        tap((_) => {
          this.router.navigate(['reference-value-sheet', 'create']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  editRefValSheetNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.editRefValSheetNavigation),
        tap((action) => {
          this.router.navigate(['reference-value-sheet', 'edit', action.refValSheet.id]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  viewRefValSheetNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.viewRefValSheetNavigation),
        tap((action) => {
          this.router.navigate(['reference-value-sheet', 'view', action.refValSheet.id]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  /* UPSERT EFFECTS */
  upsertRefValSheetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.upsertRefValSheetRequest),
      exhaustMap(({ refValSheet }) =>
        this.refValSheetService.upsertReferenceValueSheet(refValSheet).pipe(
          map((response: UpsertRefValSheetResponse) =>
            refValSheetActions.upsertRefValSheetSuccess({
              refValSheet: {
                ...refValSheet,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.upsertRefValSheetError({ error }),
              refValSheetActions.loadRefValSheetsRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  copyRefValSheetAsNewRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.copyRefValSheetAsNewRequest),
      exhaustMap(({ refValSheet }) =>
        this.refValSheetService.copyReferenceValueSheetAsNew(refValSheet.id).pipe(
          map((response: UpsertRefValSheetResponse) =>
            refValSheetActions.upsertRefValSheetSuccess({
              refValSheet: {
                ...refValSheet,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.upsertRefValSheetError({ error }),
              refValSheetActions.loadRefValSheetsRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  upsertRefValSheetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.upsertRefValSheetSuccess),
        tap((_) => {
          this.router.navigate(['reference-value-sheet']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  setColumnSettings$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.setColumnSettings),
        tap((action) => {
          if (action.save === true) {
            this.settingsService.setSettings(
              PoultryContextKeys.RVS_COLUMN_SETTINGS,
              action.value,
              SettingsStorage.Global,
              true,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  /* DELETE EFFECTS */
  deleteRefValSheetConfirm: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.deleteRefValSheetConfirm),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            text: this.locale.translate(`reference-value-sheet.dialog.deleteConfirm`),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  refValSheetActions.deleteRefValSheetRequest({
                    refValSheetId: action.refValSheet.id,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteRefValSheetRequest: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.deleteRefValSheetRequest),
      mergeMap((action) =>
        this.refValSheetService.deleteReferenceValueSheet(action.refValSheetId).pipe(
          map(() =>
            refValSheetActions.deleteRefValSheetSuccess({ refValSheetId: action.refValSheetId }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.deleteRefValSheetError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
